const { basename } = require('path');

function pluginManager(app, pluginOptions = {}) {
  const { plugins } = pluginOptions;
  const loadedPlugins = new Map();
  const loadedPluginNames = new Map();

  async function loadPlugins(pluginsConfig = null, eventKey = 'all') {
    const pluginsList = pluginsConfig || plugins || new Map();
    await app.events.emitAsync(`plugin:${eventKey}:load:start`);
    const pluginsLoaded = await loadPluginCollection(pluginsList);
    await emitPluginLoadedEvents(pluginsLoaded);
    await app.events.emitAsync(`plugin:${eventKey}:load:end`);
    return pluginsLoaded;
  }

  async function emitPluginLoadedEvents(pluginsLoaded) {
    const events = pluginsLoaded.map(plugin =>
      app.events.emitAsync(`plugin:${plugin.name}:load:loaded`, plugin)
    );
    return Promise.all(events);
  }

  async function loadPluginCollection(pluginsList) {
    const pluginsLoaded = [];
    // load in order so that dependancies can be managed
    // eslint-disable-next-line no-restricted-syntax, guard-for-in, no-unused-vars
    for (const [plugin, options] of pluginsList.entries()) {
      // eslint-disable-next-line no-await-in-loop
      const loadedPlugin = await load(plugin, options);
      if (loadedPlugin) {
        pluginsLoaded.push(loadedPlugin);
      }
    }
    return pluginsLoaded;
  }

  async function load(plugin, options = {}) {
    const pluginName = getPluginName(plugin);
    if (!pluginName) {
      app.log.error('Plugin has no name');
      return false;
    }

    if (isLoaded(null, plugin)) {
      app.log.error(`Plugin already loaded: ${plugin}`);
      return false;
    }

    const loadedPlugin = await loadPlugin(plugin, options);
    if (!loadedPlugin) {
      return false;
    }

    loadedPlugins.set(plugin, loadedPlugin.name);
    loadedPluginNames.set(loadedPlugin.name, loadedPlugin);
    await app.events.emitAsync(`plugin:${loadedPlugin.name}:load:end`);
    return loadedPlugin;
  }

  async function loadPlugin(plugin, options) {
    const loadPluginFn = typeof plugin === 'function' ? plugin : requirePlugin(plugin);
    if (!loadPluginFn) return;

    const pluginName = getPluginName(loadPluginFn, plugin);
    try {
      await app.events.emitAsync(`plugin:${pluginName}:load:start`);
      const loadedPlugin = await loadPluginFn(app, options);
      loadedPlugin.name = pluginName;
      return loadedPlugin;
    } catch (err) {
      app.log.error(`Plugin not loaded: ${pluginName} - %o`, err);
    }
    return false;
  }

  function requirePlugin(plugin) {
    try {
      const pathParts = convertPathSlashed(plugin).split('/node_modules/');
      const finalModule = pathParts[pathParts.length - 1];
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(finalModule);
    } catch (err) {
      try {
        // eslint-disable-next-line global-require,import/no-dynamic-require
        return require(plugin);
      } catch (errBase) {
        app.log.error(`Plugin not found: ${plugin} - %o`, errBase);
        app.log.error(`Original error for plugin not found: ${plugin} - %s`, err.stack);
      }
    }
  }

  function getPluginName(plugin, path = '') {
    if (typeof plugin === 'function') {
      return plugin.pluginName || basename(convertPathSlashed(path));
    }
    return basename(convertPathSlashed(plugin));
  }

  function isLoaded(name, plugin = '') {
    if (plugin) {
      if (typeof plugin === 'function') {
        return loadedPlugins.has(plugin) || loadedPluginNames.has(plugin.pluginName);
      }
      return loadedPlugins.has(plugin);
    }
    return loadedPluginNames.has(name);
  }

  function get(name) {
    return loadedPluginNames.get(name);
  }

  function getLoadedPlugins() {
    return [...loadedPluginNames.keys()];
  }

  return {
    isLoaded,
    load,
    loadPlugins,
    get,
    getLoadedPlugins
  };
}

module.exports = pluginManager;
function convertPathSlashed(plugin) {
  return plugin.replace(/\\/g, '/');
}
